/**
 * The vocabulary shared by `touristic_notification_schedule` and `touristic_notification_open`.
 *
 * WHY THIS IS A MODULE AND NOT TWO LITERALS AT THE CALL SITES, which is how every other event in
 * this app is written: these two are a CONTRACT WITH iOS, not a local choice. One dashboard card
 * reads both platforms through a single pair of event names, so a parameter that drifts on web
 * silently drops web out of the funnel — the same way the `client_id` bug in `AnalyticsService`
 * went unnoticed for months. The `media` -> `reel` mapping below is fired from two different places
 * (the scan, and the notification tap), and a rule applied in one and forgotten in the other splits
 * one bucket into two on the dashboard with nothing to indicate it happened.
 *
 * ⚠️ iOS IS THE REFERENCE AND CANNOT MOVE. Its build is approved and shipping, so every mismatch is
 * resolved on this side. @see TouristicNotificationScheduler.swift in FeatherMenu-iOS.
 */

/** iOS `TouristicNotificationKind.analyticsType`. Web's own model says `media` where iOS says `reel`. */
export type TouristicAnalyticsType = 'event' | 'reel' | 'attraction';

/** iOS `TouristicNotificationSlot.analyticsTimingType`. Slot 1 is immediate, slot 2 is delayed. */
export type TouristicAnalyticsTiming = 'immediate' | 'delayed';

/**
 * Map a payload/slot `kind` onto the `type` iOS reports.
 *
 * `media` is the Gulli media banner — the reel — and iOS has always called it `reel`. Anything
 * unrecognised passes through untouched rather than being coerced to a default: an unknown kind is
 * a sender we do not know about, which is the thing worth noticing on the dashboard, and a silent
 * fallback would file it under a real bucket.
 */
export function touristicAnalyticsType(kind: string | null | undefined): string | null {
  if (!kind) {
    return null;
  }
  return kind === 'media' ? 'reel' : kind;
}

/**
 * The query-string keys the service worker uses to carry a notification tap into the app.
 *
 * DELIBERATELY THE SAME NAMES AS THE PAYLOAD `data` KEYS, so the service worker can forward what it
 * already holds without knowing any of the vocabulary above — it copies three values and this file
 * stays the only place that understands them. `firebase-messaging-sw.js` is plain JS outside the
 * Angular build and cannot import this module, so those three strings are the one thing duplicated
 * across the boundary; keeping them identical to the payload keys is what makes the duplication
 * self-evident rather than arbitrary.
 */
export const TOURISTIC_OPEN_PARAMS = ['touristic_kind', 'timing_type', 'id'] as const;

/** What a notification tap carried, or null when this navigation was not one. */
export interface TouristicOpen {
  type: string;
  timing_type?: string;
  id?: string;
}

/**
 * Read a notification tap out of a URL's query string.
 *
 * Returns null unless `touristic_kind` is present, so an ordinary navigation costs one lookup and
 * logs nothing.
 */
export function readTouristicOpen(search: string): TouristicOpen | null {
  const params = new URLSearchParams(search);
  const type = touristicAnalyticsType(params.get('touristic_kind'));

  if (!type) {
    return null;
  }

  const open: TouristicOpen = { type };

  const timing = params.get('timing_type');
  if (timing) {
    open.timing_type = timing;
  }

  // iOS omits `id` for the attraction kind because its payload has none. Web usually has one, and a
  // present id is strictly more useful, so it is sent whenever it exists — `AnalyticsService.clean()`
  // drops it when it does not. The asymmetry with iOS is permanent and deliberate.
  const id = params.get('id');
  if (id) {
    open.id = id;
  }

  return open;
}

/**
 * The same URL with the markers removed, for `history.replaceState`.
 *
 * Stripping them is what stops a refresh logging a second open for one tap. Returns null when there
 * was nothing to strip, so the caller can skip the history write entirely.
 */
export function stripTouristicOpen(url: string): string | null {
  const parsed = new URL(url, window.location.origin);

  if (!parsed.searchParams.has('touristic_kind')) {
    return null;
  }

  TOURISTIC_OPEN_PARAMS.forEach(key => parsed.searchParams.delete(key));

  return parsed.pathname + parsed.search + parsed.hash;
}
